import { memo } from 'react';
import { alpha, Box, Chip, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MfaMethod } from 'modules/authentication/services/mfa.types';

export type MfaSetupMethod = Extract<MfaMethod, 'TOTP' | 'EMAIL'>;

export type MfaMethodSelectorProps = {
  value: MfaSetupMethod;
  onChange: (method: MfaSetupMethod) => void;
  email?: string;
  disabled?: boolean;
};

export const maskEmail = (email?: string) => {
  if (!email) return undefined;
  const [name, domain] = email.split('@');
  if (!domain || name.length < 2) return email;
  return `${name[0]}${'•'.repeat(Math.min(Math.max(name.length - 2, 1), 3))}${name[name.length - 1]}@${domain}`;
};

const MfaMethodSelector = ({ value, onChange, email, disabled }: MfaMethodSelectorProps) => {
  const { t } = useTranslation('authentication');
  const maskedEmail = maskEmail(email);

  const options: Array<{ method: MfaSetupMethod; description: string; recommended?: boolean }> = [
    {
      method: 'TOTP',
      description: t('mfa.methodChooser.totpDescription'),
      recommended: true,
    },
    {
      method: 'EMAIL',
      description: maskedEmail
        ? t('mfa.methodChooser.emailDescription', { email: maskedEmail })
        : t('mfa.methodChooser.emailDescriptionNoAddress'),
    },
  ];

  return (
    <RadioGroup
      value={value}
      onChange={(event) => { onChange(event.target.value as MfaSetupMethod); }}
      sx={{ gap: 1.5 }}
    >
      {options.map(({ method, description, recommended }) => {
        const selected = value === method;

        return (
          <Box
            key={method}
            component='label'
            sx={{
              display: 'flex',
              gap: 1.5,
              p: 2,
              cursor: disabled ? 'default' : 'pointer',
              borderRadius: 2,
              border: '2px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              bgcolor: ({ palette }) => (selected ? alpha(palette.primary.main, 0.06) : 'background.paper'),
              transition: 'border-color .15s, background-color .15s',
              opacity: disabled ? 0.6 : 1,
              '&:hover': { borderColor: selected || disabled ? undefined : 'text.disabled' },
            }}
          >
            <Radio value={method} disabled={disabled} sx={{ p: 0, mt: '2px' }} />
            <Stack spacing={0.5}>
              <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                <Typography fontWeight={600} color='text.primary'>
                  {t(`mfa.methods.${method}`)}
                </Typography>
                {recommended && (
                  <Chip
                    size='small'
                    label={t('mfa.methodChooser.recommended')}
                    sx={{
                      height: 22,
                      fontWeight: 600,
                      color: 'primary.main',
                      bgcolor: ({ palette }) => alpha(palette.primary.main, 0.12),
                    }}
                  />
                )}
              </Stack>
              <Typography variant='body2' color='text.secondary'>
                {description}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </RadioGroup>
  );
};

export default memo(MfaMethodSelector);
